import React, {useCallback, useState} from 'react';
import {
  AccessibilityRole,
  GestureResponderEvent,
  Platform,
  Text,
  TextStyle,
  TouchableWithoutFeedback,
  View,
  ViewProps,
  StyleSheet,
} from 'react-native';
import {openExternalLink, sleep} from '../../pipes';
import {downloadFileToDisk} from '../../pipes/fileHelpers';
import {useDispatch, useSelector} from 'react-redux';
import {profileTokenSelector} from '../../../redux/selectors/profile';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../snackbar/model';
import FileViewer from 'react-native-file-viewer';

interface ScreenParams {
  [key: string]: string | number | ScreenParams;
}

interface LinkProps {
  href?: string;
  testID: string;
  navigate?: (screen: string, params: ScreenParams) => void;
  screen?: string;
  style?: ViewProps['style'];
  textStyle?: TextStyle;
  external?: boolean;
  download?: boolean;
  fileName?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  screenParams?: ScreenParams;
  dataSetClass?: string;
}

export const Link: React.FC<LinkProps> = ({
  external,
  screen,
  href,
  testID,
  navigate,
  style,
  textStyle,
  children,
  screenParams,
  download,
  fileName,
  accessibilityRole = 'link',
  accessibilityLabel = '',
  dataSetClass,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(profileTokenSelector());
  const [fileLoading, setFileLoading] = useState(false);
  const pressHandler = useCallback(
    async (event: GestureResponderEvent) => {
      if (screen && navigate) {
        const params = {
          ...screenParams,
        } as ScreenParams;
        if (href) {
          params.url = href;
        }
        event.preventDefault();
        navigate(screen, params);
      } else if (external && href) {
        event.preventDefault();
        await openExternalLink(href);
      } else if (href && download) {
        const downloadedFileName = fileName || Date.now().toString();
        try {
          setFileLoading(true);
          dispatch(
            addNotification({
              message: `Началась загрузка файла "${fileName}"`,
              type: SnackbarNotificationType.Info,
            }),
          );
          await sleep(200);

          const result = await downloadFileToDisk(href, downloadedFileName, token || undefined);
          if (Platform.OS !== 'web' && FileViewer) {
            await FileViewer.open(result, {showOpenWithDialog: true});
          }
        } catch (e) {
          dispatch(
            addNotification({
              message: e?.message ?? 'Ошибка при загрузке файла',
              type: SnackbarNotificationType.Error,
            }),
          );
        } finally {
          setFileLoading(false);
        }
      }
    },
    [screen, href],
  );

  const externalHrefObj = !download ? {href} : {};

  const dataSet = {
    dataSet: {
      class: dataSetClass ?? 'link',
    },
  };

  const localStyle = StyleSheet.flatten([style, {opacity: fileLoading ? 0.36 : 1}]);

  return (
    <TouchableWithoutFeedback testID={testID} onPress={pressHandler} disabled={fileLoading}>
      <View
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
        style={localStyle}
        {...externalHrefObj}
        {...dataSet}>
        {typeof children === 'string' ? <Text style={textStyle}>{children}</Text> : children}
      </View>
    </TouchableWithoutFeedback>
  );
};
